function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatUsdCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatPct(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function formatCompactNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n);
}

function getLiquidityChangePct(liquidity) {
  const points = asArray(liquidity && liquidity.points);
  const first = toNum(points[0] && points[0].liquidity_usd);
  const last = toNum((liquidity && liquidity.latest && liquidity.latest.liquidity_usd) || (points.length ? points[points.length - 1].liquidity_usd : 0));
  if (!(first > 0) || !Number.isFinite(last)) return null;
  return ((last - first) / first) * 100;
}

function getPriceChangePct(priceHistory) {
  const points = asArray(priceHistory && priceHistory.points);
  const first = toNum(points[0] && points[0].price_usd);
  const last = toNum(points.length ? points[points.length - 1].price_usd : 0);
  if (!(first > 0) || !Number.isFinite(last)) return null;
  return ((last - first) / first) * 100;
}

export function deriveWhaleActivity(data) {
  const activity = data && data.activity ? data.activity : {};
  const transfers = asArray(activity.largestTransfers);
  const theses = asArray(data && data.theses && data.theses.theses);
  const stories = asArray(data && data.stories && data.stories.stories);
  const metadata = data && data.metadata ? data.metadata : null;

  const transferAmounts = transfers
    .map((row) => toNum(row && row.amount_usd))
    .filter((n) => n > 0);

  const maxTransferUsd = transferAmounts.length ? Math.max(...transferAmounts) : 0;
  const totalTransferUsd = transferAmounts.reduce((sum, value) => sum + value, 0);
  const storyWhaleMentions = stories.filter((row) => {
    const hay = `${row && row.title ? row.title : ''} ${row && row.subtitle ? row.subtitle : ''}`.toLowerCase();
    return hay.includes('whale') || hay.includes('accumul') || hay.includes('cluster');
  }).length;
  const thesisConviction = theses.reduce((sum, row) => sum + toNum(row && row.conviction), 0);
  const liquidityUsd = toNum(data && data.liquidity && data.liquidity.latest && data.liquidity.latest.liquidity_usd);
  const marketCapUsd = toNum(metadata && metadata.market_cap_usd);

  let score = 0;
  if (maxTransferUsd >= 1000000) score += 45;
  else if (maxTransferUsd >= 250000) score += 30;
  else if (maxTransferUsd >= 50000) score += 15;
  if (storyWhaleMentions > 0) score += Math.min(20, storyWhaleMentions * 8);
  if (thesisConviction > 0) score += Math.min(20, thesisConviction * 8);
  if (liquidityUsd > 0 && totalTransferUsd > 0) {
    const ratio = totalTransferUsd / Math.max(liquidityUsd, 1);
    score += clamp(ratio * 20, 0, 15);
  }
  if (marketCapUsd > 0 && maxTransferUsd > 0) {
    const mcapRatio = maxTransferUsd / Math.max(marketCapUsd, 1);
    score += clamp(mcapRatio * 200, 0, 20);
  }
  score = clamp(Math.round(score), 0, 100);

  const band = score >= 70 ? 'high' : score >= 40 ? 'medium' : score > 0 ? 'low' : 'none';
  const tone = band === 'high' ? 'hot' : band === 'medium' ? 'warning' : band === 'low' ? 'positive' : 'neutral';
  const headline = band === 'high'
    ? 'Whale activity detected'
    : band === 'medium'
      ? 'Moderate large-holder activity'
      : band === 'low'
        ? 'Early whale hints'
        : 'No whale signal yet';

  return {
    score,
    band,
    tone,
    headline,
    metrics: {
      maxTransferUsd,
      totalTransferUsd,
      storyWhaleMentions,
      thesisConviction,
    },
    summary: maxTransferUsd > 0
      ? `Largest tracked move ${formatUsdCompact(maxTransferUsd)} with ${storyWhaleMentions} narrative mentions.`
      : 'No large transfers in the current activity inputs.',
  };
}

export function deriveSignalStripMetrics(data, overrides) {
  if (Array.isArray(overrides) && overrides.length) return overrides;

  const metadata = data && data.metadata ? data.metadata : null;
  const liquidity = data && data.liquidity ? data.liquidity : null;
  const stories = asArray(data && data.stories && data.stories.stories);
  const theses = asArray(data && data.theses && data.theses.theses);
  const whale = deriveWhaleActivity(data);
  const liquidityChangePct = getLiquidityChangePct(liquidity);
  const priceChangePct = getPriceChangePct(data && data.priceHistory);
  const highScoreStories = stories.filter((row) => toNum(row && row.score) >= 0.7).length;
  const convictionAvg = theses.length ? theses.reduce((sum, row) => sum + toNum(row && row.conviction), 0) / theses.length : 0;

  return [
    {
      label: 'Whale Activity',
      value: whale.band === 'none' ? '—' : `${whale.score}/100`,
      tone: whale.tone,
      meta: whale.summary,
    },
    {
      label: 'Price Change',
      value: priceChangePct == null ? '—' : formatPct(priceChangePct),
      tone: priceChangePct == null ? 'neutral' : priceChangePct >= 0 ? 'positive' : 'hot',
      meta: metadata && metadata.symbol ? `${metadata.symbol} over selected range` : 'No price trend yet',
    },
    {
      label: 'Liquidity Change',
      value: liquidityChangePct == null ? '—' : formatPct(liquidityChangePct),
      tone: liquidityChangePct == null ? 'neutral' : liquidityChangePct >= 0 ? 'positive' : 'warning',
      meta: liquidity && liquidity.latest && liquidity.latest.liquidity_usd ? `${formatUsdCompact(liquidity.latest.liquidity_usd)} current liquidity` : 'No liquidity trend yet',
    },
    {
      label: 'Narrative Signals',
      value: String(stories.length),
      tone: stories.length > 0 ? 'hot' : 'neutral',
      meta: highScoreStories > 0 ? `${highScoreStories} high-score stories` : 'No linked stories yet',
    },
    {
      label: 'Thesis Conviction',
      value: theses.length ? `${Math.round(convictionAvg * 100)}%` : '—',
      tone: convictionAvg >= 0.66 ? 'positive' : convictionAvg >= 0.4 ? 'warning' : 'neutral',
      meta: theses.length ? `${theses.length} active theses` : 'No linked theses yet',
    },
    {
      label: 'Supply',
      value: formatCompactNumber(metadata && metadata.supply),
      tone: 'neutral',
      meta: metadata && metadata.updated_at ? `Metadata updated ${metadata.updated_at}` : 'No supply snapshot',
    },
  ];
}

export function deriveTokenIntelligenceScore(data, overrides) {
  if (overrides && typeof overrides === 'object' && Number.isFinite(Number(overrides.score))) {
    return overrides;
  }

  const metadata = data && data.metadata ? data.metadata : null;
  const stories = asArray(data && data.stories && data.stories.stories);
  const theses = asArray(data && data.theses && data.theses.theses);
  const whale = deriveWhaleActivity(data);
  const liquidityUsd = toNum(data && data.liquidity && data.liquidity.latest && data.liquidity.latest.liquidity_usd);
  const marketCapUsd = toNum(metadata && metadata.market_cap_usd);
  const liquidityChangePct = getLiquidityChangePct(data && data.liquidity);
  const priceChangePct = getPriceChangePct(data && data.priceHistory);

  const liquidityDepth = clamp(
    Math.round(
      (liquidityUsd > 0 && marketCapUsd > 0 ? Math.min(1, liquidityUsd / Math.max(marketCapUsd * 0.25, 1)) * 70 : 0) +
      (liquidityChangePct != null ? clamp(30 + liquidityChangePct, 0, 30) : 10)
    ),
    0,
    100
  );

  const activityLevel = clamp(
    Math.round(
      Math.min(50, whale.score * 0.5) +
      Math.min(20, stories.length * 4) +
      (priceChangePct != null ? Math.min(30, Math.abs(priceChangePct) * 1.2) : 0)
    ),
    0,
    100
  );

  const narrativeStrength = clamp(
    Math.round(
      Math.min(50, stories.length * 8) +
      Math.min(30, theses.length * 10) +
      Math.min(20, stories.filter((row) => toNum(row && row.score) >= 0.7).length * 10)
    ),
    0,
    100
  );

  const walletDistribution = clamp(
    Math.round(55 + Math.min(20, theses.length * 4) - Math.min(25, whale.score * 0.2)),
    0,
    100
  );

  const totalScore = clamp(
    Math.round(
      liquidityDepth * 0.3 +
      walletDistribution * 0.22 +
      activityLevel * 0.24 +
      narrativeStrength * 0.24
    ),
    0,
    100
  );

  return {
    score: totalScore,
    band: totalScore >= 75 ? 'strong' : totalScore >= 55 ? 'watch' : totalScore >= 35 ? 'speculative' : 'weak',
    components: {
      liquidityDepth,
      walletDistribution,
      activityLevel,
      narrativeStrength,
    },
    explanation: {
      liquidityDepth: liquidityUsd > 0 ? `Liquidity snapshot ${formatUsdCompact(liquidityUsd)}` : 'Liquidity still sparse',
      walletDistribution: whale.score >= 70 ? 'Whale concentration risk elevated' : 'No dominant whale signal yet',
      activityLevel: priceChangePct != null ? `Price move ${formatPct(priceChangePct)}` : 'Limited price history',
      narrativeStrength: stories.length || theses.length ? `${stories.length} stories and ${theses.length} theses linked` : 'Narrative still forming',
    },
  };
}

export function deriveNarrativeSummary(data, overrides) {
  if (overrides && typeof overrides === 'object' && typeof overrides.summary === 'string' && overrides.summary.trim()) {
    return {
      summary: overrides.summary,
      confidence: overrides.confidence || 'Medium',
      keySignals: asArray(overrides.keySignals),
      riskSignals: asArray(overrides.riskSignals),
    };
  }

  const metadata = data && data.metadata ? data.metadata : null;
  const stories = asArray(data && data.stories && data.stories.stories);
  const theses = asArray(data && data.theses && data.theses.theses);
  const whale = deriveWhaleActivity(data);
  const score = deriveTokenIntelligenceScore(data);
  const priceChangePct = getPriceChangePct(data && data.priceHistory);
  const liquidityChangePct = getLiquidityChangePct(data && data.liquidity);

  const symbol = metadata && metadata.symbol ? String(metadata.symbol) : 'This token';
  const summaryParts = [];
  if (priceChangePct != null) summaryParts.push(`${symbol} moved ${formatPct(priceChangePct)} over the selected range.`);
  if (liquidityChangePct != null) summaryParts.push(`Liquidity changed ${formatPct(liquidityChangePct)} in the same window.`);
  if (whale.band !== 'none') summaryParts.push(`${whale.headline.toLowerCase()} with a score of ${whale.score}/100.`);
  if (stories.length || theses.length) summaryParts.push(`${stories.length} stories and ${theses.length} theses currently shape the narrative context.`);
  if (!summaryParts.length) summaryParts.push('Narrative context is still limited while intelligence inputs warm up.');

  const keySignals = [
    whale.band !== 'none' ? whale.headline : '',
    priceChangePct != null ? `Price trend ${formatPct(priceChangePct)}` : '',
    liquidityChangePct != null ? `Liquidity trend ${formatPct(liquidityChangePct)}` : '',
    stories.length ? `${stories.length} linked stories` : '',
    theses.length ? `${theses.length} linked theses` : '',
    `E3D score ${score.score}/100`,
  ].filter(Boolean);

  const riskSignals = [
    whale.score >= 70 ? 'Whale concentration may distort short-term price action' : '',
    toNum(data && data.liquidity && data.liquidity.latest && data.liquidity.latest.liquidity_usd) <= 0 ? 'Liquidity visibility is limited' : '',
    !stories.length && !theses.length ? 'Narrative coverage is currently sparse' : '',
    score.components.walletDistribution < 45 ? 'Wallet distribution quality is weak' : '',
  ].filter(Boolean);

  const confidence = stories.length + theses.length >= 4 ? 'Medium-High' : stories.length + theses.length >= 2 ? 'Medium' : 'Low';

  return {
    summary: summaryParts.join(' '),
    confidence,
    keySignals,
    riskSignals,
  };
}
